import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Modal,
  Button,
  Select,
  DatePicker,
  InputNumber,
  Card,
  Switch,
  Table,
  message,
} from "antd";
import {
  CalculatorOutlined,
  PrinterOutlined,
  SaveOutlined,
  DollarOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import { TClient } from "~/types";
import { calculateCubicVolume, getClientName } from "~/utils";
import { StorageInvoicePrintContent } from "./storage-invoice-print-content";
import { useCreateStorageInvoice } from "~/services/storage/storage.queries";
import {
  StorageInvoice,
  StorageInvoiceLineItem,
} from "~/services/storage/storage.api";

interface StorageInvoiceModalProps {
  visible: boolean;
  onClose: () => void;
  client: TClient | null;
  allClients?: TClient[];
  existingInvoice?: StorageInvoice | null;
}

interface LocationCharge {
  locationId: string;
  locationName: string;
  itemCount: number;
  actualCubicFt: number;
  minimumCubicFt: number;
  billableCubicFt: number;
  ratePerCubicFt: number;
  amount: number;
}

interface InvoiceLineItem {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface StorageInvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  billingPeriod: string;
  client: TClient;
  lineItems: InvoiceLineItem[];
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  notes: string;
}

export const StorageInvoiceModal: React.FC<StorageInvoiceModalProps> = ({
  visible,
  onClose,
  client: initialClient,
  allClients = [],
  existingInvoice,
}) => {
  const [selectedClient, setSelectedClient] = useState<TClient | null>(
    initialClient
  );
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(dayjs());
  const [isCalculating, setIsCalculating] = useState(false);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Location-based charges
  const [locationCharges, setLocationCharges] = useState<LocationCharge[]>([]);

  // Insurance
  const [includeInsurance, setIncludeInsurance] = useState<boolean>(false);
  const [insuranceRate, setInsuranceRate] = useState<number>(0.01);
  const [inventoryValue, setInventoryValue] = useState<number>(0);

  // Create invoice mutation
  const createInvoiceMutation = useCreateStorageInvoice();

  // Calculated totals
  const storageTotal = useMemo(
    () => locationCharges.reduce((sum, loc) => sum + loc.amount, 0),
    [locationCharges]
  );

  const insuranceAmount = useMemo(
    () => (includeInsurance ? inventoryValue * insuranceRate : 0),
    [includeInsurance, inventoryValue, insuranceRate]
  );

  const totalAmount = useMemo(
    () => storageTotal + insuranceAmount,
    [storageTotal, insuranceAmount]
  );

  // Print ref
  const printRef = useRef<HTMLDivElement>(null);

  // Generate invoice data for printing
  const invoiceData: StorageInvoiceData | null = useMemo(() => {
    if (!selectedClient || !hasCalculated) return null;

    const lineItems: InvoiceLineItem[] = locationCharges.map((loc) => ({
      description: `Storage - ${loc.locationName} (${loc.billableCubicFt.toFixed(2)} ft³ @ $${loc.ratePerCubicFt.toFixed(2)}/ft³)${loc.billableCubicFt > loc.actualCubicFt ? ' [min applied]' : ''}`,
      quantity: 1,
      rate: loc.ratePerCubicFt,
      amount: loc.amount,
    }));

    if (includeInsurance && insuranceAmount > 0) {
      lineItems.push({
        description: `Storage Insurance (${(insuranceRate * 100).toFixed(2)}% of $${inventoryValue.toFixed(2)} inventory value)`,
        quantity: 1,
        rate: insuranceAmount,
        amount: insuranceAmount,
      });
    }

    return {
      invoiceNumber: `${selectedClient.clientRef || selectedClient.id.slice(0, 6).toUpperCase()}_${selectedMonth.format("MMMM")}_${selectedMonth.format("YYYY")}_ArtStorage`,
      invoiceDate: dayjs().format("MM/DD/YYYY"),
      billingPeriod: selectedMonth.format("MMMM YYYY"),
      client: selectedClient,
      lineItems,
      subtotal: totalAmount,
      taxRate: 0,
      taxAmount: 0,
      total: totalAmount,
      notes: "Payment due upon receipt. Thank you for storing with Art Handler Collective.",
    };
  }, [
    selectedClient,
    hasCalculated,
    selectedMonth,
    locationCharges,
    includeInsurance,
    insuranceRate,
    inventoryValue,
    insuranceAmount,
    totalAmount,
  ]);

  // Print handler
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: invoiceData
      ? `Storage_Invoice_${invoiceData.invoiceNumber}`
      : "Storage_Invoice",
  });

  // Reset state when modal opens or load existing invoice
  useEffect(() => {
    if (visible) {
      if (existingInvoice) {
        // Load existing invoice data
        const client = allClients.find((c) => c.id === existingInvoice.clientId);
        setSelectedClient(client || null);
        setSelectedMonth(
          dayjs()
            .year(existingInvoice.billingPeriodYear)
            .month(existingInvoice.billingPeriodMonth - 1)
        );

        // Convert line items back to location charges
        const storageItems = existingInvoice.lineItems.filter(
          (item) => item.type === "storage"
        );
        const insuranceItem = existingInvoice.lineItems.find(
          (item) => item.type === "insurance"
        );

        const charges: LocationCharge[] = storageItems.map((item) => ({
          locationId: item.locationId || "no-location",
          locationName: item.locationName,
          itemCount: 0,
          actualCubicFt: item.actualCubicFt || 0,
          minimumCubicFt: item.minimumCubicFt || 0,
          billableCubicFt: item.billableCubicFt || 0,
          ratePerCubicFt: item.ratePerCubicFt || 0,
          amount: item.amount,
        }));

        setLocationCharges(charges);
        setIncludeInsurance(!!insuranceItem);
        if (insuranceItem) {
          setInventoryValue(existingInvoice.insuranceTotal / 0.01);
          setInsuranceRate(0.01);
        }
        setHasCalculated(true);
      } else {
        // Reset for new invoice
        setSelectedClient(initialClient);
        setSelectedMonth(dayjs());
        setHasCalculated(false);
        setLocationCharges([]);
        setIncludeInsurance(false);
        setInsuranceRate(0.01);
        setInventoryValue(0);
      }
    }
  }, [visible, initialClient, existingInvoice, allClients]);

  // Calculate storage from client inventory grouped by location
  const handleCalculate = () => {
    if (!selectedClient) {
      message.warning("Please select a client first");
      return;
    }

    setIsCalculating(true);

    try {
      const inventory = selectedClient.inventoryClient || [];
      const parentItems = inventory.filter((inv) => !inv.parentInventoryId);

      // Group by location
      const byLocation = parentItems.reduce((acc, item) => {
        const locationId = item.location?.id || "no-location";
        if (!acc[locationId]) {
          acc[locationId] = {
            location: item.location,
            items: [],
          };
        }
        acc[locationId].items.push(item);
        return acc;
      }, {} as Record<string, { location?: any; items: any[] }>);

      // Calculate charges per location
      const charges: LocationCharge[] = Object.entries(byLocation).map(
        ([locationId, { location, items }]) => {
          const actualCubicFt = items.reduce((sum, item) => {
            return sum + calculateCubicVolume(item) / 1728;
          }, 0);

          const minimumCubicFt = Number(location?.minimumCubicFeet) || 0;
          const billableCubicFt = Math.max(actualCubicFt, minimumCubicFt);
          const ratePerCubicFt = Number(location?.ratePerCubicFt) || 0;
          const amount = billableCubicFt * ratePerCubicFt;

          return {
            locationId,
            locationName: location?.name || "No Location",
            itemCount: items.length,
            actualCubicFt: Math.round(actualCubicFt * 100) / 100,
            minimumCubicFt,
            billableCubicFt: Math.round(billableCubicFt * 100) / 100,
            ratePerCubicFt,
            amount: Math.round(amount * 100) / 100,
          };
        }
      );

      // Calculate total inventory value
      const totalValue = parentItems.reduce((acc, item) => {
        return acc + (Number(item.value) || 0);
      }, 0);

      setLocationCharges(charges);
      setInventoryValue(totalValue);
      setHasCalculated(true);

      message.success("Storage calculated from current inventory");
    } catch (error) {
      console.error("Error calculating storage:", error);
      message.error("Failed to calculate storage");
    } finally {
      setIsCalculating(false);
    }
  };

  // Update a location charge field
  const updateLocationCharge = (
    locationId: string,
    field: keyof LocationCharge,
    value: number
  ) => {
    setLocationCharges((prev) =>
      prev.map((loc) => {
        if (loc.locationId !== locationId) return loc;

        const updated = { ...loc, [field]: value };

        // Recalculate billable and amount if relevant fields changed
        if (field === "actualCubicFt" || field === "minimumCubicFt") {
          updated.billableCubicFt = Math.max(
            updated.actualCubicFt,
            updated.minimumCubicFt
          );
          updated.amount =
            Math.round(updated.billableCubicFt * updated.ratePerCubicFt * 100) /
            100;
        } else if (field === "billableCubicFt" || field === "ratePerCubicFt") {
          updated.amount =
            Math.round(updated.billableCubicFt * updated.ratePerCubicFt * 100) /
            100;
        }

        return updated;
      })
    );
  };

  // Save invoice to database
  const handleSave = async () => {
    if (!invoiceData || !selectedClient) {
      message.warning("Please calculate the invoice first");
      return;
    }

    // Don't save if viewing an existing invoice
    if (existingInvoice) {
      message.info("This invoice has already been saved");
      return;
    }

    setIsSaving(true);

    try {
      // Convert location charges to line items for storage
      const lineItems: StorageInvoiceLineItem[] = locationCharges.map((loc) => ({
        locationId: loc.locationId !== "no-location" ? loc.locationId : undefined,
        locationName: loc.locationName,
        description: `Storage - ${loc.locationName} (${loc.billableCubicFt.toFixed(2)} ft³ @ $${loc.ratePerCubicFt.toFixed(2)}/ft³)${loc.billableCubicFt > loc.actualCubicFt ? " [min applied]" : ""}`,
        actualCubicFt: loc.actualCubicFt,
        minimumCubicFt: loc.minimumCubicFt,
        billableCubicFt: loc.billableCubicFt,
        ratePerCubicFt: loc.ratePerCubicFt,
        amount: loc.amount,
        type: "storage" as const,
      }));

      // Add insurance line item if included
      if (includeInsurance && insuranceAmount > 0) {
        lineItems.push({
          locationName: "Insurance",
          description: `Storage Insurance (${(insuranceRate * 100).toFixed(2)}% of $${inventoryValue.toFixed(2)} inventory value)`,
          amount: insuranceAmount,
          type: "insurance" as const,
        });
      }

      await createInvoiceMutation.mutateAsync({
        clientId: selectedClient.id,
        billingPeriodMonth: selectedMonth.month() + 1,
        billingPeriodYear: selectedMonth.year(),
        invoiceDate: dayjs().toISOString(),
        lineItems,
        storageTotal,
        insuranceTotal: insuranceAmount,
        totalAmount: totalAmount,
      });

      message.success("Invoice saved successfully");
      onClose();
    } catch (error) {
      console.error("Error saving invoice:", error);
      message.error("Failed to save invoice");
    } finally {
      setIsSaving(false);
    }
  };

  // Location charges table columns
  const locationColumns = [
    {
      title: "Location",
      dataIndex: "locationName",
      key: "locationName",
      render: (name: string) => (
        <span className="flex items-center gap-1">
          <EnvironmentOutlined className="text-gray-400" />
          {name}
        </span>
      ),
    },
    {
      title: "Items",
      dataIndex: "itemCount",
      key: "itemCount",
      width: 70,
      align: "center" as const,
    },
    {
      title: "Actual ft³",
      dataIndex: "actualCubicFt",
      key: "actualCubicFt",
      width: 110,
      render: (value: number, record: LocationCharge) => (
        <InputNumber
          size="small"
          value={value}
          onChange={(v) =>
            updateLocationCharge(record.locationId, "actualCubicFt", v || 0)
          }
          min={0}
          precision={2}
          style={{ width: 90 }}
        />
      ),
    },
    {
      title: "Minimum",
      dataIndex: "minimumCubicFt",
      key: "minimumCubicFt",
      width: 100,
      render: (value: number, record: LocationCharge) => (
        <InputNumber
          size="small"
          value={value}
          onChange={(v) =>
            updateLocationCharge(record.locationId, "minimumCubicFt", v || 0)
          }
          min={0}
          precision={0}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "Billable ft³",
      dataIndex: "billableCubicFt",
      key: "billableCubicFt",
      width: 100,
      render: (value: number, record: LocationCharge) => (
        <span
          className={
            value > record.actualCubicFt
              ? "text-orange-600 font-medium"
              : "font-medium"
          }
        >
          {value.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Rate",
      dataIndex: "ratePerCubicFt",
      key: "ratePerCubicFt",
      width: 100,
      render: (value: number, record: LocationCharge) => (
        <InputNumber
          size="small"
          value={value}
          onChange={(v) =>
            updateLocationCharge(record.locationId, "ratePerCubicFt", v || 0)
          }
          min={0}
          precision={2}
          prefix="$"
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      align: "right" as const,
      render: (value: number) => (
        <span className="font-medium">${value.toFixed(2)}</span>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <DollarOutlined />
          <span>
            {existingInvoice
              ? `Invoice ${existingInvoice.invoiceNumber}`
              : "Create Storage Invoice"}
          </span>
        </div>
      }
      open={visible}
      width={900}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <div className="space-y-4">
        {/* Client & Period Selection */}
        <Card size="small" title="Invoice Details">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client
              </label>
              <Select
                className="w-full"
                placeholder="Select client"
                value={selectedClient?.id}
                onChange={(value) => {
                  const client = allClients.find((c) => c.id === value);
                  setSelectedClient(client || null);
                  setHasCalculated(false);
                  setLocationCharges([]);
                }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {allClients.map((c) => (
                  <Select.Option key={c.id} value={c.id}>
                    {c.companyName || getClientName(c)}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Billing Period
              </label>
              <DatePicker
                picker="month"
                className="w-full"
                value={selectedMonth}
                onChange={(date) => date && setSelectedMonth(date)}
                format="MMMM YYYY"
              />
            </div>
          </div>

          <div className="mt-4">
            <Button
              type="primary"
              icon={<CalculatorOutlined />}
              onClick={handleCalculate}
              loading={isCalculating}
              disabled={!selectedClient}
            >
              Calculate from Inventory
            </Button>
          </div>
        </Card>

        {/* Location Charges Grid */}
        {hasCalculated && (
          <>
            <Card
              size="small"
              title={
                <span className="flex items-center gap-2">
                  <EnvironmentOutlined />
                  Storage Charges by Location
                </span>
              }
            >
              <Table
                dataSource={locationCharges}
                columns={locationColumns}
                rowKey="locationId"
                pagination={false}
                size="small"
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row className="bg-gray-50 font-medium">
                      <Table.Summary.Cell index={0} colSpan={4}>
                        Storage Subtotal
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        {locationCharges
                          .reduce((sum, loc) => sum + loc.billableCubicFt, 0)
                          .toFixed(2)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}></Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <span className="text-base">
                          ${storageTotal.toFixed(2)}
                        </span>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />

              {locationCharges.some(
                (loc) => loc.billableCubicFt > loc.actualCubicFt
              ) && (
                <p className="text-xs text-orange-600 mt-2">
                  * Orange values indicate minimum was applied (billable &gt;
                  actual)
                </p>
              )}
            </Card>

            {/* Insurance */}
            <Card size="small" title="Insurance">
              <div className="flex items-center gap-4 mb-4">
                <Switch
                  checked={includeInsurance}
                  onChange={setIncludeInsurance}
                />
                <span>Include Insurance</span>
              </div>

              {includeInsurance && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Inventory Value
                    </label>
                    <InputNumber
                      className="w-full"
                      value={inventoryValue}
                      onChange={(v) => setInventoryValue(v || 0)}
                      min={0}
                      precision={2}
                      addonBefore="$"
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) =>
                        Number(value?.replace(/\$\s?|(,*)/g, "")) || 0
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Insurance Rate
                    </label>
                    <InputNumber
                      className="w-full"
                      value={insuranceRate * 100}
                      onChange={(v) => setInsuranceRate((v || 0) / 100)}
                      min={0}
                      max={100}
                      precision={2}
                      addonAfter="%"
                    />
                  </div>
                </div>
              )}

              {includeInsurance && (
                <div className="mt-4 p-3 bg-gray-50 rounded">
                  <div className="flex justify-between">
                    <span>Insurance Amount:</span>
                    <span className="font-medium">
                      ${insuranceAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </Card>

            {/* Invoice Total */}
            <Card size="small" className="bg-blue-50">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Storage Total:</span>
                  <span>${storageTotal.toFixed(2)}</span>
                </div>
                {includeInsurance && (
                  <div className="flex justify-between text-sm">
                    <span>Insurance:</span>
                    <span>${insuranceAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-blue-200">
                  <span>Total:</span>
                  <span>${totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button onClick={onClose}>Cancel</Button>
              {!existingInvoice && (
                <Button
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={isSaving}
                >
                  Save Invoice
                </Button>
              )}
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={() => handlePrint()}
              >
                Print / Download PDF
              </Button>
            </div>
          </>
        )}

        {/* Hidden print content */}
        {invoiceData && (
          <StorageInvoicePrintContent ref={printRef} invoiceData={invoiceData} />
        )}
      </div>
    </Modal>
  );
};
